/**
 * Page-scoped multi-select pruning for the buyers list.
 *
 * <p>🔴 <b>This exists as a named function because its RETURN VALUE has to be
 * referentially stable, and that is not something a reader notices in an inline
 * arrow.</b> The pruning runs from an effect that calls `setSelectedIds`, and
 * React only bails out of a re-render when the reducer returns the <i>same
 * array reference</i> it was given. A bare `prev.filter(...)` allocates a new
 * array every time — including when it removes nothing — so the state changes
 * identity on every render.</p>
 *
 * <p>That is not a slow path, it is an unbounded one. The list page derives its
 * rows as `list.data?.data ?? []`, so while the query is in flight the rows are
 * a fresh array each render; the id array memoised from them is fresh too; the
 * effect's dependency therefore changes every render, the effect re-runs, the
 * reducer allocates again, and the render loop never settles. It does not fail
 * an assertion or time out — it exhausts the worker's heap and the process is
 * killed, which surfaces as vitest's `Worker exited unexpectedly` with zero
 * tests run. It took out two whole test files, only one of which was about
 * selection at all.</p>
 *
 * <p>So: return `prev` unchanged when nothing was dropped. The guard is the
 * identity, not the contents — `BuyersListPage.test.tsx` asserts with `toBe`
 * rather than `toEqual` for exactly that reason, and passes on `toEqual` with
 * the defect present.</p>
 */
export function pruneSelectionToVisible(
  prev: string[],
  visibleIds: readonly string[],
): string[] {
  // Nothing selected is already stable, and the common case by a distance.
  if (prev.length === 0) return prev;

  const visible = new Set(visibleIds);
  const next = prev.filter((id) => visible.has(id));

  // Same length ⇒ every id survived ⇒ hand back the identity we were given.
  return next.length === prev.length ? prev : next;
}
